import React, { useEffect, useState } from 'react';
import { AddButton } from 'components/Button';
import { useForm } from 'react-hook-form';

import { MainInfo } from './MainInfo';
import { AdditionalDetails } from './AdditionalDetails';
import { InstrumentPicker } from './Buyer/InstrumentPicker';
import { AdminStartStep } from './Admin/AdminStartStep';
import { useCreate } from '../api/create';
import { mainInfoSchema } from '../validation';
import useYupValidationResolver from 'hook/useYupValidationResolver';
import { Modal } from 'components/Modal';
import { useJobAdPreferences } from 'contexts/JobAdPreferencesContext';
import { UnvalidatedSupplierWarning } from './UnvalidatedSupplierWarning';
import { usesNutsLocations } from 'utils/jobAdLocations';
import { useAuth } from 'lib/auth';

const getDefaultFormValues = (preferences, usesNuts) => ({
  // agreementOrDps: null,
  // agreement: null,
  buyer: null,
  // dps: null,
  instrumentType: null,
  instrumentId: null,
  instrumentName: '',
  headline: '',
  role: '',
  experienceLevel: '',
  description: '',
  type: 'On site',
  hoursPerWeek: 40,
  maxRate: '',
  referenceNumber: '',
  startDate: null,
  endDate: null,
  applicationType: preferences.application_method || 'email',
  applicationEmail: '',
  applicationURL: '',
  lastDayToRespond: null,
  questionsEndDate: null,
  agreementName: '',
  buyerName: '',
  locations: [],
  places: [],
  usesNuts,
  additionalPeriods: [],
  competenceAreas: [],
  supplierID: null,
  supplierName: '',
  hideBuyer: false,
});

export const Add = ({ refetch, companyID, user }) => {
  const { account } = useAuth();
  const isSuperAdmin = !!account?.super_admin;
  const isBuyer = !isSuperAdmin && user?.Company?.Type === 'buyer';
  const hasStartStep = isBuyer || isSuperAdmin;
  const usesNuts = !isSuperAdmin && usesNutsLocations(user?.Company);

  const [step, setStep] = useState(0);
  const [buyerStage, setBuyerStage] = useState(hasStartStep ? 'instrument' : 'done');
  const [buyerFlow, setBuyerFlow] = useState(isBuyer);
  const [openModal, setOpenModal] = useState(false);
  const [showWarning, setShowWarning] = useState(false);
  const { preferences, updatePreferences } = useJobAdPreferences();

  const handleOpenModal = () => {
    if (user?.Company?.Type === 'supplier' && !user?.Company?.validated) {
      setShowWarning(true);
    } else {
      setOpenModal(true);
    }
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    resetStep();
  };

  const handleCloseWarning = () => {
    setShowWarning(false);
  };
  const resetStep = () => {
    setStep(0);
    setBuyerStage(hasStartStep ? 'instrument' : 'done');
    setBuyerFlow(isBuyer);
  };

  const { mutate } = useCreate();
  const methods = useForm({
    defaultValues: getDefaultFormValues(preferences, usesNuts),
    resolver: useYupValidationResolver(mainInfoSchema(step)),
  });

  const handleAdminNext = ({ mode, buyer, supplier, instrumentType, instrumentId, instrumentName }) => {
    const isBuyerMode = mode === 'buyer';
    methods.setValue('buyer', buyer.company_id);
    methods.setValue('buyerName', buyer.Name || '');
    methods.setValue('usesNuts', isBuyerMode && usesNutsLocations(buyer));
    if (isBuyerMode) {
      methods.setValue('instrumentType', instrumentType);
      methods.setValue('instrumentId', instrumentId);
      methods.setValue('instrumentName', instrumentName);
    } else {
      methods.setValue('supplierID', supplier.company_id);
      methods.setValue('supplierName', supplier.Name || '');
    }
    setBuyerFlow(isBuyerMode);
    setBuyerStage('done');
    setStep(1);
  };

  const onSubmit = async (values) => {
    if (step === 0) {
      setStep(step + 1);
    } else {
      mutate(values, {
        onSuccess: () => {
          if (!isSuperAdmin) {
            updatePreferences({ application_method: values.applicationType });
          }
          refetch();
          handleCloseModal();
          resetStep();
          methods.reset();
        },
      });
    }
  };

  useEffect(() => {
    if (!openModal) {
      methods.reset({ ...getDefaultFormValues(preferences, usesNuts) });
    }
  }, [openModal, preferences, methods, usesNuts]);


  useEffect(() => {
    if (step === 1) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }

    return () => {
      document.body.style.overflow = '';
    };
  }, [step]);

  return (
    <>
      <AddButton
        fullWidth
        onClick={handleOpenModal}
        sx={({ palette }) => ({
          svg: { mt: -0.5 },
          minWidth: 200,
          border: `1px solid ${palette.grey[200]}`,
        })}
        color="secondary"
      >
        Add Advertisement
      </AddButton>
      <Modal open={showWarning} handleClose={handleCloseWarning} title="Unvalidated Supplier">
        <UnvalidatedSupplierWarning companyName={user?.Company?.Name} />
      </Modal>

      {openModal && isBuyer && buyerStage === 'instrument' && (
        <InstrumentPicker
          onCancel={handleCloseModal}
          onNext={({ instrumentType, instrumentId, instrumentName }) => {
            methods.setValue('instrumentType', instrumentType);
            methods.setValue('instrumentId', instrumentId);
            methods.setValue('instrumentName', instrumentName);
            methods.setValue('buyer', companyID);
            methods.setValue('buyerName', user?.Company?.Name || '');
            setBuyerStage('done');
            setStep(1);
          }}
        />
      )}
      {openModal && isSuperAdmin && buyerStage === 'instrument' && (
        <AdminStartStep onCancel={handleCloseModal} onNext={handleAdminNext} />
      )}
      {!hasStartStep && step === 0 && (
        <MainInfo
          companyID={companyID}
          open={openModal}
          handleCloseModal={handleCloseModal}
          methods={methods}
          onSubmit={onSubmit}
        />
      )}
      {(!hasStartStep || buyerStage === 'done') && step === 1 && (
        <AdditionalDetails
          companyID={companyID}
          buyerFlow={buyerFlow}
          methods={methods}
          onSubmit={onSubmit}
          handleCloseModal={handleCloseModal}
        />
      )}
    </>
  );
};
